/**
 * ResetWStudioData — the tenant WStudio data reset (WSRPDM-0049, WSRAPI-0081).
 *
 * This is the most destructive operation in the product: it permanently deletes
 * everything a tenant has downloaded from WStudio, with no undo, no soft-delete,
 * no recycle bin. The API enforces its own guards and confirmation, so this
 * component cannot cause an accidental reset by getting the UI wrong — its job
 * is to make sure the operator UNDERSTANDS what they are about to do (typed-slug
 * confirmation, an accurate account of what goes/stays, and the GoHighLevel
 * qualifier). None of that copy is decoration; do not trim it.
 *
 * Gated on a SUCCESSFUL preview call (`getResetPreview`) — neither reset
 * endpoint exists on any deployed environment today, so a 404 (or any other
 * failure) renders nothing at all, silently. No version check, no error
 * banner: a missing destructive feature is a non-event, and an error banner
 * about a reset endpoint invites someone to go looking for it.
 *
 * Rendered as a SIBLING of `WStudioSettings`'s `<Box component="form">`, not
 * inside it — a destructive action must not depend on `type="button"`
 * discipline to avoid a stray submit; living outside the form removes that
 * risk entirely.
 */
import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { ApiError } from '@/api/errors';
import {
  getResetPreview,
  resetTenant,
  type ResetBlockedErrorBody,
  type ResetPreview,
  type ResetReceipt,
} from '@/api/wsr/tenantReset';
import { wsrApiError } from '@/api/wsr/errors';
import { useCopy } from '@/hooks/useCopy';

type TFn = ReturnType<typeof useCopy>['t'];

/** §4a of the spec — the Architect's answer to §11.2: four headline tables,
 *  everything else is diagnostic detail behind the disclosure. Reused from
 *  `warehouse.*` — same four WStudio-sourced datasets, already localized. */
const HEADLINE_TABLES: Array<{ key: string; labelKey: string; fallback: string }> = [
  { key: 'wstudio_user', labelKey: 'warehouse.members', fallback: 'WStudio members' },
  { key: 'wstudio_sale', labelKey: 'warehouse.sales', fallback: 'Sales' },
  { key: 'wstudio_attendance', labelKey: 'warehouse.attendance', fallback: 'Attendance' },
  { key: 'wstudio_product', labelKey: 'warehouse.products', fallback: 'Products' },
];
const HEADLINE_KEYS = new Set(HEADLINE_TABLES.map((table) => table.key));

/** §3 — explicit lookup, fail-closed per the WSRPDM-0041/0042 convention: an
 *  unrecognised `blockedBy` reason renders the raw wire value rather than
 *  being hidden or throwing. */
const BLOCKED_REASON_COPY: Record<string, { key: string; fallback: string }> = {
  TENANT_ENABLED: {
    key: 'reset.blocked.tenantEnabled',
    fallback: 'Disable this tenant before resetting it.',
  },
  JOBS_IN_FLIGHT: {
    key: 'reset.blocked.jobsInFlight',
    fallback: 'Wait for running jobs to finish, then try again.',
  },
};

function blockedReasonText(reason: string, t: TFn): string {
  const copy = BLOCKED_REASON_COPY[reason];
  return copy ? t(copy.key, copy.fallback) : reason;
}

function CountsBlock({
  counts,
  t,
  showHeading = true,
}: {
  counts: Record<string, number>;
  t: TFn;
  showHeading?: boolean;
}) {
  const [showAll, setShowAll] = useState(false);
  const extraEntries = Object.entries(counts).filter(([key]) => !HEADLINE_KEYS.has(key));

  return (
    <Box>
      {showHeading ? (
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
          {t('reset.willDelete', 'This will permanently delete:')}
        </Typography>
      ) : null}
      <List dense disablePadding>
        {HEADLINE_TABLES.map((table) => (
          <ListItem key={table.key} disablePadding>
            <ListItemText
              primary={`${t(table.labelKey, table.fallback)}: ${counts[table.key] ?? 0}`}
            />
          </ListItem>
        ))}
      </List>
      {extraEntries.length > 0 ? (
        <Box sx={{ mt: 1 }}>
          <Button size="small" onClick={() => setShowAll((current) => !current)}>
            {t('reset.showAllTables', 'Show every table')}
          </Button>
          <Collapse in={showAll} unmountOnExit>
            <List dense disablePadding sx={{ mt: 0.5 }}>
              {extraEntries.map(([key, count]) => (
                <ListItem key={key} disablePadding>
                  <ListItemText
                    slotProps={{ primary: { variant: 'body2' } }}
                    primary={`${key}: ${count}`}
                  />
                </ListItem>
              ))}
            </List>
          </Collapse>
        </Box>
      ) : null}
    </Box>
  );
}

type Outcome =
  | { kind: 'success'; receipt: ResetReceipt }
  | { kind: 'blocked'; blockedBy: string[] }
  | { kind: 'mismatch' }
  | { kind: 'error' }
  | null;

interface ResetWStudioDataProps {
  slug: string;
  onLoad: () => void;
  onSummaryRefresh: () => void;
}

export function ResetWStudioData({ slug, onLoad, onSummaryRefresh }: ResetWStudioDataProps) {
  const { t } = useCopy();
  const [previewStatus, setPreviewStatus] = useState<'loading' | 'hidden' | 'ready'>('loading');
  const [preview, setPreview] = useState<ResetPreview | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [alsoDeleteConfig, setAlsoDeleteConfig] = useState(false);
  const [typedSlug, setTypedSlug] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [outcome, setOutcome] = useState<Outcome>(null);

  useEffect(() => {
    let cancelled = false;
    getResetPreview(slug)
      .then((result) => {
        if (cancelled) return;
        setPreview(result);
        setPreviewStatus('ready');
      })
      .catch(() => {
        // §2: gate on a successful preview call — any failure (404 because
        // WSRAPI-0081 isn't deployed, or anything else) hides the section
        // entirely. Never surface a load error for it.
        if (!cancelled) setPreviewStatus('hidden');
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  // Background refresh only — used when the dialog opens and after a 409.
  // A failure here must not hide an already-visible section or invent a new
  // error state; it just keeps the last-known preview.
  const refreshPreview = () => {
    getResetPreview(slug)
      .then((result) => setPreview(result))
      .catch(() => {
        /* keep the last-known preview */
      });
  };

  if (previewStatus !== 'ready' || !preview) return null;

  const openDialog = () => {
    setDialogOpen(true);
    setTypedSlug('');
    setAlsoDeleteConfig(false);
    setOutcome(null);
    refreshPreview();
  };

  const closeDialog = () => setDialogOpen(false);

  const handleConfirm = async () => {
    setSubmitting(true);
    try {
      const receipt = await resetTenant(slug, {
        confirmTenantSlug: typedSlug,
        alsoDeleteWStudioConfig: alsoDeleteConfig,
      });
      setOutcome({ kind: 'success', receipt });
      onLoad();
      onSummaryRefresh();
    } catch (error) {
      const status = error instanceof ApiError ? error.status : undefined;
      if (status === 409) {
        const body = wsrApiError(error) as ResetBlockedErrorBody | null;
        setOutcome({ kind: 'blocked', blockedBy: body?.blockedBy ?? [] });
        refreshPreview();
      } else if (status === 400) {
        setOutcome({ kind: 'mismatch' });
      } else {
        setOutcome({ kind: 'error' });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const slugMatches = typedSlug.trim() === slug;
  const confirmDisabled = !slugMatches || preview.resettable !== true || submitting;
  const isSuccess = outcome?.kind === 'success';

  return (
    <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, borderColor: 'error.main' }}>
      <Typography variant="h2" sx={{ fontSize: '1.2rem', fontWeight: 600, mb: 1 }}>
        {t('reset.title', 'Reset WStudio data')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t(
          'reset.description',
          'Permanently delete everything this tenant has downloaded from WStudio. This cannot be undone.',
        )}
      </Typography>
      <Button
        type="button"
        color="error"
        variant="outlined"
        onClick={openDialog}
        disabled={!preview.resettable}
      >
        {t('reset.button', 'Reset WStudio data')}
      </Button>
      {!preview.resettable ? (
        <Stack spacing={0.5} sx={{ mt: 1 }}>
          {preview.blockedBy.map((reason) => (
            <Typography key={reason} variant="body2" color="text.secondary">
              {blockedReasonText(reason, t)}
            </Typography>
          ))}
        </Stack>
      ) : null}

      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {isSuccess
            ? t('reset.successTitle', 'Reset complete')
            : t('reset.title', 'Reset WStudio data')}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            {isSuccess && outcome ? (
              <>
                <CountsBlock counts={outcome.receipt.counts} t={t} showHeading={false} />
                <Alert severity="success">
                  {t(
                    'reset.successNextSteps',
                    'This tenant is now unverified and nothing will run. Run the dry-run tests, then enable it to begin loading history again.',
                  )}
                </Alert>
              </>
            ) : (
              <>
                {outcome?.kind === 'blocked' ? (
                  <Alert severity="warning">
                    <Stack spacing={0.5}>
                      {outcome.blockedBy.map((reason) => (
                        <Typography key={reason} variant="body2">
                          {blockedReasonText(reason, t)}
                        </Typography>
                      ))}
                      <Typography variant="body2">
                        {t('reset.nothingDeleted', 'Nothing was deleted.')}
                      </Typography>
                    </Stack>
                  </Alert>
                ) : null}
                {outcome?.kind === 'mismatch' ? (
                  <Alert severity="error">
                    <Typography variant="body2">
                      {t('reset.confirmMismatch', 'The tenant name did not match.')}
                    </Typography>
                    <Typography variant="body2">
                      {t('reset.nothingDeleted', 'Nothing was deleted.')}
                    </Typography>
                  </Alert>
                ) : null}
                {outcome?.kind === 'error' ? (
                  <Alert severity="error">
                    <Typography variant="body2">
                      {t('errors.generic', 'Something went wrong. Please try again.')}
                    </Typography>
                    <Typography variant="body2">
                      {t('reset.nothingDeleted', 'Nothing was deleted.')}
                    </Typography>
                  </Alert>
                ) : null}

                <CountsBlock counts={preview.counts} t={t} />

                <Typography variant="body2">
                  {t(
                    'reset.kept',
                    'Kept: the tenant itself, its sync schedule and enable switches, report recipients, and the audit history.',
                  )}
                </Typography>

                <Typography variant="body2">
                  {t(
                    'reset.ghlNote',
                    'GoHighLevel is not touched. Contacts, custom fields and tags written for these members stay in GoHighLevel and must be removed there.',
                  )}
                </Typography>

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={alsoDeleteConfig}
                      onChange={(event) => setAlsoDeleteConfig(event.target.checked)}
                    />
                  }
                  label={t('reset.alsoDeleteConfig', 'Also clear WStudio connection settings')}
                />
                <Typography variant="caption" color="text.secondary">
                  {t(
                    'reset.alsoDeleteConfigHelp',
                    'You will need to re-enter the domain, token and Studio ID before this tenant can sync again.',
                  )}
                </Typography>

                <TextField
                  label={t('reset.confirmLabel', 'Type {{slug}} to confirm', { slug })}
                  value={typedSlug}
                  onChange={(event) => setTypedSlug(event.target.value)}
                  helperText={slug}
                  fullWidth
                />
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          {isSuccess ? (
            <Button onClick={closeDialog}>{t('common.close', 'Close')}</Button>
          ) : (
            <>
              <Button onClick={closeDialog}>{t('common.cancel', 'Cancel')}</Button>
              <Button
                onClick={() => void handleConfirm()}
                color="error"
                variant="contained"
                disabled={confirmDisabled}
              >
                {t('reset.confirmButton', 'Permanently delete')}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
